import { useState } from "react";
import { PawPrint, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PetShareCard } from "@/components/PetShareCard";

const QUESTIONS = [
  { key: "pet_type", label: "Who's coming along?", options: [
    { value: "dogs", label: "Dog(s)" }, { value: "cats", label: "Cat(s)" }, { value: "both", label: "Both" }] },
  { key: "mode", label: "How will they travel?", options: [
    { value: "driving", label: "Driving with us" }, { value: "flying", label: "Flying" }] },
  { key: "timeline", label: "When's the move?", options: [
    { value: "soon", label: "Within 3 months" }, { value: "3-6_months", label: "3–6 months" }, { value: "later", label: "Just planning" }] },
];

export const PetQuiz = () => {
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const complete = QUESTIONS.every((q) => answers[q.key]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/leads/checklist", { email, source: "pet_quiz", answers });
      setItems(data.items);
      toast.success("Checklist sent — check your inbox!");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div data-testid="pet-quiz" className="rounded-3xl border border-sand-200 bg-white p-8 shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
      <p className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-terracotta">
        <PawPrint size={16} /> Free pet move checklist
      </p>
      <h2 className="mt-2 text-2xl sm:text-3xl font-normal text-charcoal-800">Is your pet ready for the move?</h2>
      <p className="mt-2 text-base text-charcoal-600">Answer 3 quick questions and we'll build a personalized checklist for your cat or dog — and email you a copy.</p>

      {items ? (
        <div data-testid="pet-quiz-result" className="mt-6">
          <p className="inline-flex items-center gap-2 text-sm font-medium text-sage"><Check size={15} /> Sent to {email} — here it is:</p>
          <ul className="mt-4 space-y-2">
            {items.map((it, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl bg-sand-100 px-4 py-3 text-sm text-charcoal-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage-700"><Check size={12} /></span>
                {it}
              </li>
            ))}
          </ul>
          <PetShareCard petType={answers.pet_type} />
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-5">
          {QUESTIONS.map((q) => (
            <div key={q.key}>
              <p className="mb-2 text-sm font-medium text-charcoal-700">{q.label}</p>
              <div className="flex flex-wrap gap-2">
                {q.options.map((o) => (
                  <button type="button" key={o.value} data-testid={`quiz-${q.key}-${o.value}`}
                    onClick={() => setAnswers((a) => ({ ...a, [q.key]: o.value }))}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${answers[q.key] === o.value ? "border-terracotta bg-terracotta text-white" : "border-sand-300 bg-sand-100 text-charcoal-700 hover:border-terracotta"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {complete && (
            <div className="flex flex-col gap-3 sm:flex-row">
              <input data-testid="quiz-email" type="email" required placeholder="Your email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-full border border-sand-300 bg-white px-5 py-3 text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50" />
              <button data-testid="quiz-submit" type="submit" disabled={busy}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} Get my checklist
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
};
